/**
 * Create Branch - Club admin form for creating new branches under their club
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, FormInput, FormSelect, FormTextArea, AdminPageLayout, Section, Card, Grid, Col, Badge, PhoneNumberInput } from '../../components';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/api';

const CreateBranch = () => {
  const navigate = useNavigate();
  const { club_id } = useParams();
  const { showSuccess, showError } = useToast();

  const [clubInfo, setClubInfo] = useState(null);
  const [adminMode, setAdminMode] = useState('skip'); // 'skip', 'create', 'assign'
  const [potentialAdmins, setPotentialAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);

  const [formData, setFormData] = useState(() => {
    const isDevelopment = process.env.NODE_ENV === 'development';

    return {
      name: isDevelopment ? 'Richmond Training Facility' : '',
      display_name: isDevelopment ? 'Richmond' : '',
      location_type: 'training_ground',
      description: isDevelopment ? 'Main training facility for youth programs' : '',
      website: '',
      email: isDevelopment ? 'richmond@club.com' : '',
      phone: isDevelopment ? '(555) 123-4567' : '',
      address_line1: isDevelopment ? '123 Training Lane' : '',
      address_line2: '',
      city: isDevelopment ? 'Richmond' : '',
      state: isDevelopment ? 'VA' : '',
      zip_code: isDevelopment ? '23220' : '',
      country: 'US',
      timezone: 'America/New_York',
      currency: 'USD',
      inherit_club_settings: true,
      age_calculation_method: 'calendar_year',
      age_cutoff_date: null,
      admin_first_name: '',
      admin_last_name: '',
      admin_email: '',
      admin_phone: '',
      admin_password: '',
      assign_existing_user_id: ''
    };
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadClubInfo = async () => {
      if (club_id) {
        try {
          const club = await apiService.getClub(club_id);
          setClubInfo(club);
        } catch (error) {
          console.log('Could not load club info');
        }
      }
    };
    loadClubInfo();
  }, [club_id]);

  useEffect(() => {
    if (adminMode === 'assign') {
      loadPotentialAdmins();
    }
  }, [adminMode]);

  const loadPotentialAdmins = async () => {
    setLoadingAdmins(true);
    try {
      const response = await apiService.getPotentialBranchAdmins();
      setPotentialAdmins(response.users || []);
    } catch (error) {
      showError('Failed to load potential branch administrators');
      setPotentialAdmins([]);
    } finally {
      setLoadingAdmins(false);
    }
  };

  const handleAdminModeChange = (e) => {
    const newMode = e.target.value;
    setAdminMode(newMode);
    setFormData(prev => ({
      ...prev,
      admin_first_name: '',
      admin_last_name: '',
      admin_email: '',
      admin_phone: '',
      admin_password: '',
      assign_existing_user_id: ''
    }));
    setFieldErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Branch name is required';
    if (!formData.location_type) newErrors.location_type = 'Branch type is required';
    if (!formData.email.trim()) newErrors.email = 'Contact email is required';
    if (!formData.phone.trim()) newErrors.phone = 'Contact phone is required';
    if (!formData.address_line1.trim()) newErrors.address_line1 = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.zip_code.trim()) newErrors.zip_code = 'ZIP code is required';

    if (adminMode === 'create') {
      if (!formData.admin_first_name?.trim()) newErrors.admin_first_name = 'Required';
      if (!formData.admin_last_name?.trim()) newErrors.admin_last_name = 'Required';
      if (!formData.admin_email?.trim()) newErrors.admin_email = 'Required';
      if (formData.admin_phone && formData.admin_phone.trim() && formData.admin_phone.replace(/\D/g, '').length < 10) {
        newErrors.admin_phone = 'Phone must be at least 10 digits';
      }
      if (!formData.admin_password) newErrors.admin_password = 'Required';
    } else if (adminMode === 'assign') {
      if (!formData.assign_existing_user_id) newErrors.assign_existing_user_id = 'Required';
    }

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const branchData = {};
      Object.keys(formData).forEach(key => {
        if (!key.startsWith('admin_') && key !== 'assign_existing_user_id') {
          branchData[key] = formData[key];
        }
      });

      if (adminMode === 'create') {
        branchData.admin_first_name = formData.admin_first_name;
        branchData.admin_last_name = formData.admin_last_name;
        branchData.admin_email = formData.admin_email;
        // Only send admin_phone if it has a value (backend requires min 10 chars if provided)
        if (formData.admin_phone && formData.admin_phone.trim()) {
          branchData.admin_phone = formData.admin_phone;
        }
        branchData.admin_password = formData.admin_password;
      } else if (adminMode === 'assign') {
        branchData.assign_existing_user_id = formData.assign_existing_user_id;
      }

      await apiService.createBranch(null, branchData);
      showSuccess(`Branch "${formData.name}" created successfully`);
      if (club_id) {
        navigate(`/admin/clubs/${club_id}/branches`);
      } else {
        navigate('/admin/branches');
      }
    } catch (error) {
      showError(error.message || 'Failed to create branch');
    } finally {
      setLoading(false);
    }
  };

  const locationTypeOptions = [
    { value: 'main_facility', label: 'Main Facility' },
    { value: 'branch_facility', label: 'Branch Facility' },
    { value: 'training_ground', label: 'Training Ground' },
    { value: 'academy', label: 'Academy' },
    { value: 'community_center', label: 'Community Center' }
  ];

  const ageCalculationOptions = [
    { value: 'calendar_year', label: 'Calendar Year (Age as of Dec 31)' },
    { value: 'birth_year', label: 'Birth Year' },
    { value: 'program_start', label: 'Program Start Date' },
    { value: 'registration_date', label: 'Registration Date' },
    { value: 'custom_date', label: 'Custom Cutoff Date' }
  ];

  const usStateOptions = [
    { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' }
  ];

  return (
    <AdminPageLayout
      title="Create Branch"
      subtitle={clubInfo ? `Add a new location for ${clubInfo.name}` : "Add a new branch location"}
      backPath={club_id ? `/admin/clubs/${club_id}/branches` : "/admin/branches"}
      maxWidth="1400px"
    >
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>

          <Section title="Branch Details">
            <Card level={1}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <Grid columns={2} gap="1.5rem">
                  <Col span={1}>
                    <FormInput
                      label="Branch Name *"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      error={fieldErrors.name}
                      placeholder="e.g., Richmond Training Facility"
                      required
                    />
                  </Col>
                  <Col span={1}>
                    <FormInput
                      label="Short Name"
                      name="display_name"
                      value={formData.display_name}
                      onChange={handleInputChange}
                      error={fieldErrors.display_name}
                      placeholder="e.g., Richmond"
                      helpText="Optional shorter name for display"
                    />
                  </Col>
                </Grid>

                <Grid columns={2} gap="1.5rem">
                  <Col span={1}>
                    <FormSelect
                      label="Branch Type *"
                      name="location_type"
                      value={formData.location_type}
                      onChange={handleInputChange}
                      options={locationTypeOptions}
                      error={fieldErrors.location_type}
                      required
                    />
                  </Col>
                  <Col span={1}>
                  </Col>
                </Grid>

                <FormTextArea
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  error={fieldErrors.description}
                  rows={3}
                  placeholder="Brief description of this location..."
                />
              </div>
            </Card>
          </Section>

          <Section title="Contact Information">
            <Card level={1}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <Grid columns={3} gap="1.5rem">
                  <Col span={1}>
                    <FormInput
                      label="Email *"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      error={fieldErrors.email}
                      required
                    />
                  </Col>
                  <Col span={1}>
                    <PhoneNumberInput
                      label="Phone *"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      error={fieldErrors.phone}
                      required
                    />
                  </Col>
                  <Col span={1}>
                    <FormInput
                      label="Website"
                      name="website"
                      type="url"
                      value={formData.website}
                      onChange={handleInputChange}
                      error={fieldErrors.website}
                      placeholder="https://..."
                    />
                  </Col>
                </Grid>

                <div style={{ padding: '1.5rem', background: 'var(--surface-lvl2)', borderRadius: '12px', border: '1px solid var(--color-border-light)' }}>
                  <div style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', marginBottom: '1rem' }}>Address</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                    <FormInput
                      label="Address Street"
                      name="address_line1"
                      value={formData.address_line1}
                      onChange={handleInputChange}
                      error={fieldErrors.address_line1}
                      required
                    />
                    <Grid columns={3} gap="1.5rem">
                      <Col span={1}>
                        <FormInput
                          label="City *"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          error={fieldErrors.city}
                          required
                        />
                      </Col>
                      <Col span={1}>
                        <FormSelect
                          label="State *"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          options={usStateOptions}
                          error={fieldErrors.state}
                          required
                        />
                      </Col>
                      <Col span={1}>
                        <FormInput
                          label="ZIP Code *"
                          name="zip_code"
                          value={formData.zip_code}
                          onChange={handleInputChange}
                          error={fieldErrors.zip_code}
                          required
                        />
                      </Col>
                    </Grid>
                  </div>
                </div>
              </div>
            </Card>
          </Section>

          <Section title="Settings">
            <Card level={1}>
              <Grid columns={2} gap="1.5rem">
                <Col span={1}>
                  <FormSelect
                    label="Age Calculation Method"
                    name="age_calculation_method"
                    value={formData.age_calculation_method}
                    onChange={handleInputChange}
                    options={ageCalculationOptions}
                    error={fieldErrors.age_calculation_method}
                    helpText="How player ages are calculated for eligibility"
                  />
                </Col>
                <Col span={1}>
                  {formData.age_calculation_method === 'custom_date' && (
                    <FormInput
                      label="Age Cutoff Date"
                      name="age_cutoff_date"
                      type="date"
                      value={formData.age_cutoff_date || ''}
                      onChange={handleInputChange}
                      error={fieldErrors.age_cutoff_date}
                    />
                  )}
                </Col>
              </Grid>
            </Card>
          </Section>

          <Section title="Branch Administrator">
            <Card level={1}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <FormSelect
                  label="Admin Setup"
                  name="adminMode"
                  value={adminMode}
                  onChange={handleAdminModeChange}
                  options={[
                    { value: 'skip', label: 'Assign Later' },
                    { value: 'create', label: 'Create New Admin' },
                    { value: 'assign', label: 'Assign Existing User' }
                  ]}
                />

                {adminMode === 'create' && (
                  <div style={{ padding: '1.5rem', background: 'var(--color-primary-transparent)', borderRadius: '12px', border: '1px solid var(--color-primary-light)' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                      <Grid columns={2} gap="1.5rem">
                        <Col span={1}>
                          <FormInput
                            label="First Name *"
                            name="admin_first_name"
                            value={formData.admin_first_name}
                            onChange={handleInputChange}
                            error={fieldErrors.admin_first_name}
                            required
                          />
                        </Col>
                        <Col span={1}>
                          <FormInput
                            label="Last Name *"
                            name="admin_last_name"
                            value={formData.admin_last_name}
                            onChange={handleInputChange}
                            error={fieldErrors.admin_last_name}
                            required
                          />
                        </Col>
                      </Grid>
                      <Grid columns={2} gap="1.5rem">
                        <Col span={1}>
                          <FormInput
                            label="Email *"
                            name="admin_email"
                            type="email"
                            value={formData.admin_email}
                            onChange={handleInputChange}
                            error={fieldErrors.admin_email}
                            required
                          />
                        </Col>
                        <Col span={1}>
                          <FormInput
                            label="Phone (Optional)"
                            name="admin_phone"
                            type="tel"
                            value={formData.admin_phone}
                            onChange={handleInputChange}
                            error={fieldErrors.admin_phone}
                            placeholder="(555) 123-4567"
                          />
                        </Col>
                      </Grid>
                      <Grid columns={2} gap="1.5rem">
                        <Col span={1}>
                          <FormInput
                            label="Password *"
                            name="admin_password"
                            type="password"
                            value={formData.admin_password}
                            onChange={handleInputChange}
                            error={fieldErrors.admin_password}
                            required
                            helpText="Temporary password - admin can change after first login"
                          />
                        </Col>
                        <Col span={1}></Col>
                      </Grid>
                    </div>
                  </div>
                )}

                {adminMode === 'assign' && (
                  <div style={{ padding: '1.5rem', background: 'rgba(var(--color-success-rgb), 0.05)', borderRadius: '12px', border: '1px solid var(--color-success-light)' }}>
                    {loadingAdmins ? (
                      <div style={{ textAlign: 'center', py: '1rem', opacity: 0.6 }}>Synchronizing authorized users...</div>
                    ) : (
                      <FormSelect
                        label="Select Authorized Entity"
                        name="assign_existing_user_id"
                        value={formData.assign_existing_user_id}
                        onChange={handleInputChange}
                        options={[
                          { value: '', label: 'Select a user...' },
                          ...potentialAdmins.map(user => ({
                            value: user.id,
                            label: `${user.full_name} (${user.email})`
                          }))
                        ]}
                        error={fieldErrors.assign_existing_user_id}
                        required
                      />
                    )}
                  </div>
                )}

                {adminMode === 'skip' && (
                  <div style={{ padding: '1.25rem', background: 'var(--surface-lvl2)', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <Badge variant="outline" size="sm">NO ADMIN</Badge>
                    <span style={{ fontSize: '0.9rem', opacity: 0.7 }}>Club administrators will manage this branch until you assign a dedicated admin.</span>
                  </div>
                )}
              </div>
            </Card>
          </Section>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end',
            marginTop: '1rem',
            padding: '2rem',
            background: 'var(--surface-lvl1)',
            borderRadius: '16px',
            border: '1px solid var(--color-border-light)'
          }}>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="save"
              loading={loading}
              disabled={loading}
            >
              Create Branch
            </Button>
          </div>
        </div>
      </form>
    </AdminPageLayout>
  );
};

export default CreateBranch;
